import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FORMULAS, 
  FORMULA_CATEGORIES, 
  CATEGORY_LABELS, 
  CATEGORY_DESCRIPTIONS,
  getFormulaById, 
  getFormulasByCategory,
  calculatePrice,
  calculatePriceWithPromo,
  getActivePromo,
  getDynamicSurcharge,
  BOOKING_OPTIONS,
  MAX_PLAYERS_PER_SLOT,
  checkConsecutiveSlots,
  getValidStartSlots,
  getLoyaltyLevel,
  hasFreeSession,
  LOYALTY_CONFIG,
  isSlotTooSoon,
  MIN_BOOKING_DELAY_MINUTES
} from '../config/formulas';
import { 
  PAYMENT_METHODS, PAYMENT_LABELS, BANK_INFO, createStripeCheckout 
} from '../config/payment';
import { sendBookingEmails } from '../services/emailService';
import { saveContact, getContactByEmail, PENDING_PAYMENT_MINUTES } from '../services/dataService';
import { useAdmin } from '../contexts/AdminContext';
import '../styles/BookingForm.css';

function BookingForm({ session, date, sessions, onCreateBooking, fieldMode = false }) {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();

  // Dérogation au délai minimum d'1h :
  //   - l'admin y échappe toujours (gestion des walk-ins au comptoir)
  //   - le "mode terrain" la lève pour TOUS les clients, quand Nicolas
  //     est sur place et que le matériel est déjà installé
  const bypassDelai = isAdmin || fieldMode;
  
  // États du formulaire - étapes
  const [step, setStep] = useState(1); // 1=catégorie, 2=formule, 3=créneau, 4=infos, 5=paiement, 6=récap
  
  // Données du formulaire
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFormula, setSelectedFormula] = useState(null);
  const [selectedStartSlot, setSelectedStartSlot] = useState(null);
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [nbParticipants, setNbParticipants] = useState(1);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  // Public : uniquement la carte bancaire (Stripe). Admin : garde le choix complet.
  const [paymentMethod, setPaymentMethod] = useState(
    isAdmin ? PAYMENT_METHODS.ON_SITE : PAYMENT_METHODS.STRIPE
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [stripeUrl, setStripeUrl] = useState(null); // URL Stripe pour iOS
  const [customPrice, setCustomPrice] = useState(null); // Prix personnalisé admin
  const [selectedOptions, setSelectedOptions] = useState([]); // Options additionnelles
  const [loyaltyInfo, setLoyaltyInfo] = useState(null); // Infos fidélité du client
  const [checkingLoyalty, setCheckingLoyalty] = useState(false);

  // Formules filtrées par catégorie
  const categoryFormulas = useMemo(() => {
    if (!selectedCategory) return [];
    return getFormulasByCategory(selectedCategory);
  }, [selectedCategory]);

  // Créneaux de départ valides pour la formule sélectionnée
  const validStartSlots = useMemo(() => {
    if (!selectedFormula) return [];
    const dateStr = typeof date === 'string' ? date : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const daySessions = sessions.filter(s => s.date === dateStr);
    return getValidStartSlots(selectedFormula.id, date, daySessions, bypassDelai);
  }, [selectedFormula, date, sessions, bypassDelai]);

  // Date en string
  const dateStr = typeof date === 'string' ? date : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  // Calculer le prix avec promo éventuelle
  const priceInfo = useMemo(() => {
    if (!selectedFormula) return { basePrice: 0, finalPrice: 0, promo: null, discount: 0, dynamicSurcharge: null, optionsPrice: 0 };
    const nb = parseInt(nbParticipants, 10) || selectedFormula.minPlayers;
    return calculatePriceWithPromo(selectedFormula.id, nb, dateStr, selectedOptions);
  }, [selectedFormula, nbParticipants, dateStr, selectedOptions]);

  const totalPrice = customPrice !== null ? customPrice : priceInfo.finalPrice;
  const activePromo = customPrice !== null ? null : priceInfo.promo;

  // Formater la date
  const formatDate = (d) => {
    const dateObj = typeof d === 'string' ? new Date(d) : d;
    return dateObj.toLocaleDateString('fr-FR', { 
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
    });
  };

  // Sélectionner une catégorie
  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    setSelectedFormula(null);
    setSelectedStartSlot(null);
    setOccupiedSlots([]);
    setStep(2);
  };

  // Sélectionner une formule
  const handleSelectFormula = (formula) => {
    setSelectedFormula(formula);
    setSelectedStartSlot(null);
    setOccupiedSlots([]);
    setNbParticipants(formula.minPlayers);
    setStep(3);
  };

  // Sélectionner un créneau de départ
  const handleSelectSlot = (slot) => {
    const dateStr = typeof date === 'string' ? date : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const daySessions = sessions.filter(s => s.date === dateStr);
    const check = checkConsecutiveSlots(slot, selectedFormula.slots, date, daySessions, bypassDelai);
    
    if (check.available) {
      setSelectedStartSlot(slot);
      setOccupiedSlots(check.slots);
      setStep(4);
    } else {
      // On explique au client pourquoi le créneau est refusé
      alert(check.reason || "Ce créneau n'est pas disponible.");
    }
  };

  // Soumettre la réservation
  const handleSubmit = async () => {
    if (!contactName) {
      alert("Veuillez entrer un nom de contact.");
      return;
    }

    // Sécurité : le temps a pu passer pendant que le client remplissait le formulaire
    if (!bypassDelai && isSlotTooSoon(date, selectedStartSlot)) {
      alert(`Ce créneau n'est plus réservable (moins de ${MIN_BOOKING_DELAY_MINUTES} min avant le début). Merci de choisir un autre horaire.`);
      setSelectedStartSlot(null);
      setOccupiedSlots([]);
      setStep(3);
      return;
    }

    // Sécurité : forcer un nombre valide si le champ a été laissé vide
    const finalNbParticipants = parseInt(nbParticipants, 10) || selectedFormula.minPlayers;

    setIsSubmitting(true);
    
    const dateStr = typeof date === 'string' ? date : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    const bookingData = {
      formulaId: selectedFormula.id,
      formulaName: selectedFormula.name,
      category: selectedFormula.category,
      date: dateStr,
      startTime: selectedStartSlot,
      slots: occupiedSlots,
      nbParticipants: finalNbParticipants,
      basePrice: priceInfo.basePrice,
      totalPrice,
      optionsPrice: priceInfo.optionsPrice || 0,
      selectedOptions: selectedOptions.map(o => {
        const fullOption = BOOKING_OPTIONS.find(bo => bo.id === o.id);
        const qty = o.quantity || 1;
        let subtotal = 0;
        if (fullOption) {
          if (fullOption.priceType === 'per_person') {
            subtotal = fullOption.price * finalNbParticipants;
          } else if (fullOption.priceType === 'per_unit') {
            subtotal = fullOption.price * qty;
          } else {
            subtotal = fullOption.price;
          }
        }
        return {
          id: o.id,
          name: o.name,
          quantity: qty,
          price: fullOption?.price || 0,
          priceType: fullOption?.priceType || 'flat',
          subtotal
        };
      }),
      dynamicSurcharge: priceInfo.dynamicSurcharge?.reason || null,
      loyaltyLevel: loyaltyInfo?.level?.name || null,
      loyaltyFreeSession: loyaltyInfo?.isFreeSession || false,
      customPrice: customPrice !== null ? true : false,
      originalPrice: priceInfo.finalPrice,
      promoApplied: activePromo ? activePromo.name : null,
      promoDiscount: activePromo ? priceInfo.discount : 0,
      contactName,
      contactPhone,
      contactEmail,
      notes,
      isPaid: paymentMethod === PAYMENT_METHODS.STRIPE ? false : isPaid,
      paymentMethod: paymentMethod,
      // Paiement en ligne → réservation provisoire : la place est bloquée
      // 20 min, le temps de payer. Elle ne devient définitive qu'au retour
      // du webhook Stripe. Sinon elle est supprimée automatiquement.
      status: paymentMethod === PAYMENT_METHODS.STRIPE ? 'pending_payment' : 'confirmed'
    };

    try {
      const result = await onCreateBooking(bookingData);
      
      if (result) {
        // Sauvegarder le contact dans la base clients (en arrière-plan)
        const bookingWithId = { ...bookingData, id: result.id || result };
        saveContact(bookingWithId).catch(err => console.error('Erreur sauvegarde contact:', err));

        // Emails : envoyés seulement si la réservation est déjà confirmée.
        // Pour un paiement Stripe, ils partent après validation du paiement
        // (sinon le client recevrait une confirmation sans avoir payé).
        sendBookingEmails(bookingWithId).then(emailResult => {
          if (emailResult.clientSent) console.log('Email client envoyé');
          if (emailResult.adminSent) console.log('Notification admin envoyée');
        }).catch(err => console.error('Erreur emails:', err));

        // Si paiement Stripe → obtenir l'URL et afficher le bouton de paiement
        if (paymentMethod === PAYMENT_METHODS.STRIPE) {
          setPaymentLoading(true);
          try {
            const checkout = await createStripeCheckout(bookingWithId);
            
            if (checkout.url) {
              // Stocker l'URL pour afficher le bouton (compatible iOS)
              setStripeUrl(checkout.url);
              setStep(7); // Étape spéciale : page de redirection vers Stripe
              return;
            }
          } catch (stripeErr) {
            alert(`Erreur lors de la création du paiement. Votre réservation n'a pas pu être confirmée, la place sera relibérée dans ${PENDING_PAYMENT_MINUTES} minutes. Merci de réessayer ou de nous contacter par téléphone.`);
            console.error("Erreur Stripe:", stripeErr);
          } finally {
            setPaymentLoading(false);
          }
        }
        
        // Pour les autres modes, retour à la vue journalière
        navigate(`/daily/${dateStr}`);
      } else {
        alert("Erreur lors de la création de la réservation.");
      }
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigation entre étapes
  const goBack = () => {
    if (step === 1) {
      const dateStr = typeof date === 'string' ? date : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      navigate(`/daily/${dateStr}`);
    } else {
      setStep(step - 1);
    }
  };

  return (
    <div className="booking-form-container">
      {/* En-tête */}
      <div className="booking-header">
        <button onClick={goBack} className="back-button">
          &lt; Retour
        </button>
        <h2>Nouvelle réservation</h2>
        <div className="booking-date">{formatDate(date)}</div>
      </div>

      {/* Indicateur d'étapes */}
      <div className="steps-indicator">
        {[
          { num: 1, label: 'Catégorie' },
          { num: 2, label: 'Formule' },
          { num: 3, label: 'Horaire' },
          { num: 4, label: 'Infos' },
          { num: 5, label: 'Paiement' },
          { num: 6, label: 'Récap' }
        ].map(s => (
          <div 
            key={s.num} 
            className={`step ${step >= s.num ? 'active' : ''} ${step === s.num ? 'current' : ''}`}
          >
            <div className="step-number">{s.num}</div>
            <div className="step-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ÉTAPE 1 : Choix de la catégorie */}
      {step === 1 && (
        <div className="step-content">
          <h3>Quel type de réservation ?</h3>
          <div className="category-cards">
            {Object.entries(FORMULA_CATEGORIES).map(([key, value]) => (
              <div 
                key={value}
                className={`category-card ${selectedCategory === value ? 'selected' : ''}`}
                onClick={() => handleSelectCategory(value)}
              >
                <div className="category-icon">
                  {value === 'individual' && '🎯'}
                  {value === 'group' && '👥'}
                  {value === 'event' && '🎉'}
                </div>
                <div className="category-name">{CATEGORY_LABELS[value]}</div>
                <div className="category-description">{CATEGORY_DESCRIPTIONS[value]}</div>
                <div className="category-count">
                  {getFormulasByCategory(value).length} formule(s)
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ÉTAPE 2 : Choix de la formule */}
      {step === 2 && (
        <div className="step-content">
          <h3>Choisir une formule - {CATEGORY_LABELS[selectedCategory]}</h3>
          <div className="formula-cards">
            {categoryFormulas.map(formula => (
              <div 
                key={formula.id}
                className={`formula-card ${selectedFormula?.id === formula.id ? 'selected' : ''}`}
                style={{ borderLeftColor: formula.color }}
                onClick={() => handleSelectFormula(formula)}
              >
                <div className="formula-header-row">
                  <span className="formula-icon">{formula.icon}</span>
                  <span className="formula-name">{formula.name}</span>
                </div>
                <div className="formula-description">{formula.description}</div>
                <div className="formula-details">
                  <div className="formula-detail">
                    <span className="detail-label">Parties</span>
                    <span className="detail-value">{formula.parties}</span>
                  </div>
                  <div className="formula-detail">
                    <span className="detail-label">Sur place</span>
                    <span className="detail-value">~{formula.duration >= 60 ? `${Math.floor(formula.duration/60)}h${formula.duration%60 > 0 ? formula.duration%60 : ''}` : `${formula.duration} min`}</span>
                  </div>
                  <div className="formula-detail">
                    <span className="detail-label">Créneaux</span>
                    <span className="detail-value">{formula.slots} × 30 min</span>
                  </div>
                  <div className="formula-detail formula-price-detail">
                    <span className="detail-label">Prix</span>
                    <span className="detail-value price">
                      {formula.price}€
                      {formula.priceType === 'per_person' && <small>/pers.</small>}
                    </span>
                  </div>
                </div>
                <div className="formula-players">
                  {formula.minPlayers} à {formula.maxPlayers} joueurs
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ÉTAPE 3 : Choix du créneau de départ */}
      {step === 3 && selectedFormula && (
        <div className="step-content">
          <h3>Choisir l'heure de début</h3>
          <div className="formula-reminder">
            <span className="formula-icon">{selectedFormula.icon}</span>
            <span>{selectedFormula.name} — {selectedFormula.slots} créneau(x) consécutif(s)</span>
          </div>

          {validStartSlots.length > 0 ? (
            <div className="slot-grid">
              {validStartSlots.map(slot => {
                const daySessions = sessions.filter(s => s.date === dateStr);
                const check = checkConsecutiveSlots(slot, selectedFormula.slots, date, daySessions, bypassDelai);
                
                // Calculer les places restantes sur le premier créneau
                const slotSession = daySessions.find(s => s.time === slot);
                const currentPlayers = slotSession?.participantCount || 0;
                const remaining = MAX_PLAYERS_PER_SLOT - currentPlayers;
                
                return (
                  <div 
                    key={slot}
                    className={`slot-option ${selectedStartSlot === slot ? 'selected' : ''}`}
                    style={{ borderColor: selectedStartSlot === slot ? selectedFormula.color : 'transparent' }}
                    onClick={() => handleSelectSlot(slot)}
                  >
                    <div className="slot-time">{slot}</div>
                    <div className="slot-range">
                      {slot} → {(() => {
                        const lastSlot = check.slots[check.slots.length - 1];
                        const [h, m] = lastSlot.split(':').map(Number);
                        const endMinutes = h * 60 + m + 30;
                        const endH = Math.floor(endMinutes / 60);
                        const endM = endMinutes % 60;
                        return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                      })()}
                    </div>
                    <div className={`slot-remaining ${remaining <= 3 ? 'few' : ''} ${remaining <= 0 ? 'full' : ''}`}>
                      {remaining <= 3 && remaining > 0 
                        ? `⚡ Plus que ${remaining} place${remaining > 1 ? 's' : ''} !`
                        : `${remaining} place${remaining > 1 ? 's' : ''}`
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-slots-message">
              <p>Aucun créneau disponible pour cette formule à cette date.</p>
              <p>La formule nécessite {selectedFormula.slots} créneau(x) consécutif(s).</p>
            </div>
          )}
        </div>
      )}

      {/* ÉTAPE 4 : Informations contact + nombre de participants */}
      {step === 4 && (
        <div className="step-content">
          <h3>Informations de réservation</h3>
          
          <div className="booking-info-form">
            <div className="form-group">
              <label htmlFor="nbParticipants">
                Nombre de participants 
                {isAdmin 
                  ? ' (admin : pas de limite)' 
                  : ` (${selectedFormula.minPlayers} - ${selectedFormula.maxPlayers})`
                }
              </label>
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                id="nbParticipants"
                value={nbParticipants}
                onChange={(e) => {
                  // Laisser l'utilisateur taper librement, même si la valeur est temporairement vide ou hors bornes
                  const raw = e.target.value;
                  if (raw === '') {
                    setNbParticipants('');
                    setCustomPrice(null);
                    return;
                  }
                  const val = parseInt(raw, 10);
                  if (!isNaN(val)) {
                    setNbParticipants(val);
                    setCustomPrice(null);
                  }
                }}
                onBlur={(e) => {
                  // Clamp min/max uniquement quand on sort du champ
                  const val = parseInt(e.target.value, 10);
                  const min = isAdmin ? 1 : selectedFormula.minPlayers;
                  const max = isAdmin ? 999 : selectedFormula.maxPlayers;
                  if (isNaN(val) || val < min) {
                    setNbParticipants(min);
                  } else if (val > max) {
                    setNbParticipants(max);
                  }
                }}
                min={isAdmin ? 1 : selectedFormula.minPlayers}
                max={isAdmin ? 999 : selectedFormula.maxPlayers}
                className="form-input"
              />
            </div>

            {/* Bloc prix avec possibilité de modification admin */}
            {activePromo && (
              <div className="promo-banner">
                <span className="promo-icon">🎉</span>
                <span className="promo-text">{activePromo.name} : -{activePromo.discount * 100}%</span>
              </div>
            )}

            <div className="price-preview">
              {activePromo && customPrice === null ? (
                <>
                  <span className="price-label">Prix :</span>
                  <span className="price-original">{priceInfo.basePrice}€</span>
                  <span className="price-arrow">→</span>
                  <span className="price-amount promo">{totalPrice}€</span>
                  <span className="price-saving">(-{priceInfo.discount}€)</span>
                </>
              ) : (
                <>
                  <span className="price-label">Prix total :</span>
                  <span className="price-amount">{totalPrice}€</span>
                  {selectedFormula.priceType === 'per_person' && customPrice === null && (
                    <span className="price-detail">({selectedFormula.price}€ × {nbParticipants} pers.)</span>
                  )}
                  {customPrice !== null && (
                    <span className="price-detail">(prix ajusté par l'admin)</span>
                  )}
                </>
              )}
            </div>

            {/* Admin : ajustement manuel du prix */}
            {isAdmin && (
              <div className="admin-price-override">
                <div className="admin-override-header">
                  <span className="admin-badge">🔧 Admin</span>
                  <span>Ajuster le prix manuellement</span>
                </div>
                <div className="admin-override-row">
                  <input
                    type="number"
                    value={customPrice !== null ? customPrice : priceInfo.finalPrice}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      if (!isNaN(val) && val >= 0) {
                        setCustomPrice(val);
                      }
                    }}
                    className="form-input admin-price-input"
                    step="0.01"
                    min="0"
                  />
                  <span className="admin-price-unit">€</span>
                  {customPrice !== null && (
                    <button 
                      className="admin-reset-price"
                      onClick={() => setCustomPrice(null)}
                    >
                      Réinitialiser ({priceInfo.finalPrice}€)
                    </button>
                  )}
                </div>
                {nbParticipants > selectedFormula.maxPlayers && (
                  <p className="admin-warning">
                    ⚠️ {nbParticipants} joueurs dépasse la capacité normale de {selectedFormula.maxPlayers}. 
                    Assurez-vous d'avoir la place sur le terrain.
                  </p>
                )}
              </div>
            )}

            {/* Tarif dynamique info */}
            {priceInfo.dynamicSurcharge?.reason && customPrice === null && (
              <div className="dynamic-pricing-notice">
                📅 {priceInfo.dynamicSurcharge.reason}
              </div>
            )}

            {/* Options additionnelles */}
            <div className="options-section">
              <h4>Options (facultatif)</h4>
              {BOOKING_OPTIONS.filter(o => o.enabled).map(option => {
                const isSelected = selectedOptions.some(o => o.id === option.id);
                const selectedOpt = selectedOptions.find(o => o.id === option.id);
                return (
                  <div key={option.id} className={`option-card ${isSelected ? 'selected' : ''}`}>
                    <div className="option-check" onClick={() => {
                      if (isSelected) {
                        setSelectedOptions(selectedOptions.filter(o => o.id !== option.id));
                      } else {
                        setSelectedOptions([...selectedOptions, { id: option.id, name: option.name, quantity: 1 }]);
                      }
                    }}>
                      <span className="option-checkbox">{isSelected ? '☑️' : '⬜'}</span>
                      <span className="option-icon">{option.icon}</span>
                      <div className="option-info">
                        <span className="option-name">{option.name}</span>
                        <span className="option-desc">{option.description}</span>
                      </div>
                      <span className="option-price">
                        {option.priceType === 'per_person' 
                          ? `${option.price}€/pers.`
                          : option.priceType === 'per_unit'
                            ? `${option.price}€/unité`
                            : `${option.price}€`
                        }
                      </span>
                    </div>
                    {isSelected && option.priceType === 'per_unit' && (
                      <div className="option-quantity">
                        <label>Quantité :</label>
                        <input 
                          type="number" 
                          min="1" max="20"
                          value={selectedOpt?.quantity || 1}
                          onChange={(e) => {
                            const qty = Math.max(1, parseInt(e.target.value) || 1);
                            setSelectedOptions(selectedOptions.map(o => 
                              o.id === option.id ? { ...o, quantity: qty } : o
                            ));
                          }}
                          className="quantity-input"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              {priceInfo.optionsPrice > 0 && (
                <div className="options-total">
                  Options : +{priceInfo.optionsPrice}€
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="contactName">Nom du contact *</label>
              <input
                type="text"
                id="contactName"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="form-input"
                placeholder="Prénom et nom"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactPhone">Téléphone</label>
              <input
                type="tel"
                id="contactPhone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className="form-input"
                placeholder="06 12 34 56 78"
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactEmail">Email</label>
              <input
                type="email"
                id="contactEmail"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                onBlur={async () => {
                  // Vérifier la fidélité quand le client quitte le champ email
                  if (contactEmail && contactEmail.includes('@')) {
                    setCheckingLoyalty(true);
                    try {
                      const contact = await getContactByEmail(contactEmail);
                      if (contact) {
                        const level = getLoyaltyLevel(contact.totalBookings || 0);
                        const nextBookingNum = (contact.totalBookings || 0) + 1;
                        const isFree = hasFreeSession(nextBookingNum);
                        setLoyaltyInfo({
                          contact,
                          level,
                          nextBookingNum,
                          isFreeSession: isFree
                        });
                        // Appliquer automatiquement la session gratuite
                        if (isFree && selectedFormula?.id === LOYALTY_CONFIG.freeSessionFormula) {
                          setCustomPrice(0);
                        }
                      } else {
                        setLoyaltyInfo(null);
                      }
                    } catch (err) {
                      console.log('Fidélité non vérifiable');
                    } finally {
                      setCheckingLoyalty(false);
                    }
                  }
                }}
                className="form-input"
                placeholder="email@exemple.fr"
              />
              {checkingLoyalty && (
                <span className="loyalty-checking">Vérification fidélité...</span>
              )}
            </div>

            {/* Bandeau fidélité */}
            {loyaltyInfo && (
              <div className={`loyalty-banner ${loyaltyInfo.isFreeSession ? 'free-session' : ''}`}>
                <div className="loyalty-header">
                  <span className="loyalty-icon">{loyaltyInfo.level.icon}</span>
                  <span className="loyalty-level" style={{ color: loyaltyInfo.level.color }}>
                    Client {loyaltyInfo.level.name}
                  </span>
                  <span className="loyalty-count">
                    {loyaltyInfo.contact.totalBookings} visite{loyaltyInfo.contact.totalBookings > 1 ? 's' : ''}
                  </span>
                </div>
                {loyaltyInfo.isFreeSession ? (
                  <div className="loyalty-free">
                    🎁 <strong>Session offerte !</strong> C'est sa {loyaltyInfo.nextBookingNum}ème réservation — 
                    la formule {LOYALTY_CONFIG.freeSessionFormula === selectedFormula?.id 
                      ? 'est offerte automatiquement !' 
                      : `Découverte est offerte ! (sélectionnez-la pour en profiter)`
                    }
                  </div>
                ) : (
                  <div className="loyalty-progress">
                    Encore {LOYALTY_CONFIG.freeSessionEvery - (loyaltyInfo.nextBookingNum % LOYALTY_CONFIG.freeSessionEvery)} visite{LOYALTY_CONFIG.freeSessionEvery - (loyaltyInfo.nextBookingNum % LOYALTY_CONFIG.freeSessionEvery) > 1 ? 's' : ''} avant la session offerte
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="notes">Notes (optionnel)</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="form-input form-textarea"
                placeholder="Informations complémentaires..."
                rows={3}
              />
            </div>

            <button 
              className="next-button"
              onClick={() => {
                if (!contactName.trim()) {
                  alert("Le nom du contact est obligatoire.");
                  return;
                }
                setStep(5);
              }}
            >
              Choisir le mode de paiement →
            </button>
          </div>
        </div>
      )}

      {/* ÉTAPE 5 : Choix du mode de paiement */}
      {step === 5 && (
        <div className="step-content">
          <h3>{isAdmin ? 'Comment souhaitez-vous payer ?' : 'Paiement'}</h3>
          
          {activePromo && (
            <div className="promo-banner">
              <span className="promo-icon">🎉</span>
              <span className="promo-text">{activePromo.name}</span>
            </div>
          )}

          <div className="price-preview">
            {activePromo ? (
              <>
                <span className="price-label">Montant :</span>
                <span className="price-original">{priceInfo.basePrice}€</span>
                <span className="price-arrow">→</span>
                <span className="price-amount promo">{totalPrice}€</span>
              </>
            ) : (
              <>
                <span className="price-label">Montant à régler :</span>
                <span className="price-amount">{totalPrice}€</span>
              </>
            )}
          </div>

          <div className="payment-methods">
            {/* Carte bancaire (Stripe) */}
            <div 
              className={`payment-method-card ${paymentMethod === PAYMENT_METHODS.STRIPE ? 'selected' : ''}`}
              onClick={() => setPaymentMethod(PAYMENT_METHODS.STRIPE)}
            >
              <div className="pm-icon">💳</div>
              <div className="pm-info">
                <div className="pm-name">Carte bancaire</div>
                <div className="pm-description">Paiement sécurisé en ligne via Stripe</div>
              </div>
              <div className="pm-radio">
                {paymentMethod === PAYMENT_METHODS.STRIPE ? '●' : '○'}
              </div>
            </div>

            {/* Sur place — réservé à l'admin */}
            {isAdmin && (
            <div 
              className={`payment-method-card ${paymentMethod === PAYMENT_METHODS.ON_SITE ? 'selected' : ''}`}
              onClick={() => setPaymentMethod(PAYMENT_METHODS.ON_SITE)}
            >
              <div className="pm-icon">🏪</div>
              <div className="pm-info">
                <div className="pm-name">Paiement sur place</div>
                <div className="pm-description">Espèces ou carte bancaire à votre arrivée</div>
              </div>
              <div className="pm-radio">
                {paymentMethod === PAYMENT_METHODS.ON_SITE ? '●' : '○'}
              </div>
            </div>
            )}

            {/* Virement bancaire — réservé à l'admin */}
            {isAdmin && (
            <div 
              className={`payment-method-card ${paymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? 'selected' : ''}`}
              onClick={() => setPaymentMethod(PAYMENT_METHODS.BANK_TRANSFER)}
            >
              <div className="pm-icon">🏦</div>
              <div className="pm-info">
                <div className="pm-name">Virement bancaire</div>
                <div className="pm-description">Virement à effectuer avant votre venue</div>
              </div>
              <div className="pm-radio">
                {paymentMethod === PAYMENT_METHODS.BANK_TRANSFER ? '●' : '○'}
              </div>
            </div>
            )}
          </div>

          {/* Public : rappel que le paiement en ligne est le seul mode proposé */}
          {!isAdmin && (
            <p className="payment-only-card-note">
              🔒 Le règlement s'effectue en ligne par carte bancaire, de façon sécurisée via Stripe.
              Votre réservation sera confirmée dès le paiement validé.
            </p>
          )}

          {/* Détails virement si sélectionné */}
          {paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && (
            <div className="bank-transfer-info">
              <h4>Coordonnées bancaires :</h4>
              <div className="bank-detail"><span>Titulaire :</span> {BANK_INFO.titulaire}</div>
              <div className="bank-detail"><span>IBAN :</span> {BANK_INFO.iban}</div>
              <div className="bank-detail"><span>BIC :</span> {BANK_INFO.bic}</div>
              <div className="bank-detail"><span>Banque :</span> {BANK_INFO.banque}</div>
              <div className="bank-detail"><span>Référence :</span> {BANK_INFO.reference}(sera complétée)</div>
              <p className="bank-note">
                Merci d'effectuer le virement au moins 48h avant votre session.
              </p>
            </div>
          )}

          <button 
            className="next-button"
            onClick={() => setStep(6)}
          >
            Voir le récapitulatif →
          </button>
        </div>
      )}

      {/* ÉTAPE 6 : Récapitulatif */}
      {step === 6 && (
        <div className="step-content">
          <h3>Récapitulatif de la réservation</h3>
          
          <div className="recap-card" style={{ borderLeftColor: selectedFormula.color }}>
            <div className="recap-section">
              <div className="recap-label">Formule</div>
              <div className="recap-value">
                {selectedFormula.icon} {selectedFormula.name}
                <span className="recap-category">({CATEGORY_LABELS[selectedCategory]})</span>
              </div>
            </div>

            <div className="recap-section">
              <div className="recap-label">Date</div>
              <div className="recap-value">{formatDate(date)}</div>
            </div>

            <div className="recap-section">
              <div className="recap-label">Horaires</div>
              <div className="recap-value">
                {occupiedSlots[0]} → {occupiedSlots[occupiedSlots.length - 1]}
                <span className="recap-detail">({selectedFormula.duration} min — {occupiedSlots.length} créneau(x))</span>
              </div>
            </div>

            <div className="recap-section">
              <div className="recap-label">Participants</div>
              <div className="recap-value">{nbParticipants} personne(s)</div>
            </div>

            <div className="recap-section">
              <div className="recap-label">Contact</div>
              <div className="recap-value">
                {contactName}
                {contactPhone && <span className="recap-detail"> — {contactPhone}</span>}
                {contactEmail && <span className="recap-detail"> — {contactEmail}</span>}
              </div>
            </div>

            {notes && (
              <div className="recap-section">
                <div className="recap-label">Notes</div>
                <div className="recap-value recap-notes">{notes}</div>
              </div>
            )}

            {/* Détail des options choisies */}
            {selectedOptions && selectedOptions.length > 0 && (
              <div className="recap-section recap-options">
                <div className="recap-label">Options</div>
                <div className="recap-value recap-options-list">
                  {selectedOptions.map(opt => {
                    const fullOption = BOOKING_OPTIONS.find(bo => bo.id === opt.id);
                    if (!fullOption) return null;
                    const qty = opt.quantity || 1;
                    let subtotal = 0;
                    if (fullOption.priceType === 'per_person') {
                      subtotal = fullOption.price * (parseInt(nbParticipants, 10) || selectedFormula.minPlayers);
                    } else if (fullOption.priceType === 'per_unit') {
                      subtotal = fullOption.price * qty;
                    } else {
                      subtotal = fullOption.price;
                    }
                    return (
                      <div key={opt.id} className="recap-option-item">
                        <span>
                          {fullOption.icon} {fullOption.name}
                          {fullOption.priceType === 'per_unit' && qty > 1 && ` ×${qty}`}
                        </span>
                        <span className="recap-option-price">+{subtotal}€</span>
                      </div>
                    );
                  })}
                  <div className="recap-option-total">
                    Total options : +{priceInfo.optionsPrice}€
                  </div>
                </div>
              </div>
            )}

            {activePromo && (
              <div className="recap-section recap-promo">
                <div className="recap-label">Promotion</div>
                <div className="recap-value recap-promo-value">
                  🎉 {activePromo.name}
                  <span className="recap-detail">-{priceInfo.discount}€ de réduction</span>
                </div>
              </div>
            )}

            <div className="recap-section recap-total">
              <div className="recap-label">Prix total</div>
              <div className="recap-value recap-price">
                {activePromo ? (
                  <>
                    <span className="price-original-small">{priceInfo.basePrice}€</span> {totalPrice}€
                  </>
                ) : (
                  <>
                    {totalPrice}€
                    {selectedFormula.priceType === 'per_person' && (
                      <span className="recap-detail">({selectedFormula.price}€ × {nbParticipants})</span>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="recap-section">
              <div className="recap-label">Paiement</div>
              <div className="recap-value">
                {paymentMethod === PAYMENT_METHODS.STRIPE && '💳 Carte bancaire (en ligne)'}
                {paymentMethod === PAYMENT_METHODS.ON_SITE && '🏪 Sur place'}
                {paymentMethod === PAYMENT_METHODS.BANK_TRANSFER && '🏦 Virement bancaire'}
              </div>
            </div>
          </div>

          <div className="recap-actions">
            <button onClick={() => setStep(5)} className="edit-button">
              ← Modifier
            </button>
            <button 
              onClick={handleSubmit} 
              className="confirm-booking-button"
              disabled={isSubmitting || paymentLoading}
              style={{ backgroundColor: selectedFormula.color }}
            >
              {paymentLoading ? 'Redirection vers le paiement...' :
               isSubmitting ? 'Enregistrement...' : 
               paymentMethod === PAYMENT_METHODS.STRIPE ? '💳 Payer et confirmer' :
               '✓ Confirmer la réservation'}
            </button>
          </div>
        </div>
      )}

      {/* ÉTAPE 7 : Redirection vers le paiement Stripe */}
      {step === 7 && stripeUrl && (
        <div className="step-content">
          <div className="stripe-redirect-page">
            <div className="stripe-icon">⏳</div>
            <h3>Dernière étape : le paiement</h3>
            <p>
              Votre créneau est <strong>bloqué pendant {PENDING_PAYMENT_MINUTES} minutes</strong>.
              Votre réservation sera définitivement confirmée dès la validation du paiement.
            </p>
            
            <a 
              href={stripeUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="stripe-pay-button"
            >
              💳 Payer {totalPrice}€ par carte bancaire
            </a>

            <p className="stripe-note">
              Vous serez redirigé vers la page de paiement sécurisée Stripe.
              Sans paiement sous {PENDING_PAYMENT_MINUTES} minutes, le créneau sera remis à la disposition d'autres clients.
            </p>

            <button 
              onClick={() => {
                if (!window.confirm(`Attention : sans paiement, votre créneau ne sera pas réservé et sera libéré dans ${PENDING_PAYMENT_MINUTES} minutes.\n\nVoulez-vous vraiment abandonner le paiement ?`)) {
                  return;
                }
                const dateStr = typeof date === 'string' ? date : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                navigate(`/daily/${dateStr}`);
              }}
              className="stripe-later-button"
            >
              Abandonner
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingForm;
